// colorWheel — 共有カラーパネル (ColorPanel) の純粋ヘルパ群 (Phase 6.7 サブ task、consult 2026-05-07).
//
// 2026-06-20: 共有カラーパネル抽出 (consult 2026-06-20) に伴い `layouts/classic/` から
// `presentation/color/` へ移設。Classic の floating `ColorWheelPalette` / 左ペイン
// `ArgbSliderPanel` と Modern の共有 `ColorPanel` が layout 非依存に共用する。
//
// 設計の核心:
// - DOM / React に触らない pure function だけをここに置き、vitest (node 環境)
//   で round-trip / 表記モード切替をテスト可能にする。
// - `HsvaColor` (= @uiw/color-convert の標準型) を internal source-of-truth に採用.
//   React 側 state は HSVA 1 個だけ持ち、表示時に各表記に変換する戦略 (= 旧版
//   `ColorMakeCanvas.isRGBMode` の 2 way が HSV / HSL / RGB の 3 way 化される)。
// - 既存 PenConfig は `{ color: rgb24, alpha: 0..255 }`. 一方 @uiw 系は
//   `HsvaColor.a` が 0..1. 境界変換は `hsvaFromRgb24Alpha255` / `rgb24FromHsva` /
//   `alpha255FromHsva` の 3 関数に閉じ込め、本ファイル内のみで完結させる.
//
// 旧版踏襲との関係 (`ColorChartDialog.java` / `ColorMakeCanvas.java`):
// - 旧版: 「dblclick → ダイアログ → 色作成 → palette / 描画色を上書き」
// - 新版: dblclick で floating palette を開き、wheel/slider 操作のたびに
//   `onColorChange(rgb24)` を即発火 (= ArgbSliderPanel と同経路, operate-as-you-go,
//   palette slot 永続化はスコープ外)
// - 旧版の RGB ⇄ HSB トグルは「右クリック (Meta+click)」だったが、新版は
//   3 way (HSV / HSL / RGB) のタブ切替に拡張する (= consult 2026-05-07 確定).

import {
  type HslaColor,
  type HsvaColor,
  hexToHsva,
  hslaToHsva,
  hsvaToHex,
  hsvaToHsla,
  hsvaToRgba,
  type RgbaColor,
  rgbaToHsva,
  validHex,
} from '@uiw/react-color';

/**
 * 数値表示モード (右下タブ). 旧版 `ColorMakeCanvas.isRGBMode` の 2 way を
 * Clip Studio 風に 3 way 化したもの (consult 2026-05-07 確定).
 */
export type NumericMode = 'hsv' | 'hsl' | 'rgb';

export const NUMERIC_MODES: ReadonlyArray<NumericMode> = ['hsv', 'hsl', 'rgb'];

/** UI 表示用ラベル (タブのテキスト). */
export const NUMERIC_MODE_LABELS: Readonly<Record<NumericMode, string>> = {
  hsv: 'HSV',
  hsl: 'HLS',
  rgb: 'RGB',
};

/* ─── PenConfig (rgb24 + alpha255) ⇄ HsvaColor (0..1 alpha) の境界変換 ──── */

/** 8bit channel 抽出. */
const r8 = (rgb: number): number => (rgb >>> 16) & 0xff;
const g8 = (rgb: number): number => (rgb >>> 8) & 0xff;
const b8 = (rgb: number): number => rgb & 0xff;

/** PenConfig の `{color: rgb24, alpha: 0..255}` を HsvaColor に変換. */
export const hsvaFromRgb24Alpha255 = (rgb24: number, alpha255: number): HsvaColor => {
  const a = clamp01(alpha255 / 255);
  const r = r8(rgb24);
  const g = g8(rgb24);
  const b = b8(rgb24);
  return rgbaToHsva({ r, g, b, a });
};

/** HsvaColor → rgb24 (0..0xFFFFFF). alpha は別途 `alpha255FromHsva` で取る. */
export const rgb24FromHsva = (hsva: HsvaColor): number => {
  const { r, g, b } = hsvaToRgba(hsva);
  return ((clamp255(r) << 16) | (clamp255(g) << 8) | clamp255(b)) >>> 0;
};

/** HsvaColor.a (0..1) → alpha255 (0..255 整数). */
export const alpha255FromHsva = (hsva: HsvaColor): number => clamp255(Math.round(hsva.a * 255));

/* ─── ArgbSliderPanel HSB toggle 用 (0..255 normalized HSV、旧版踏襲) ───── */
//
// 旧 `ColorMakeCanvas.java:467-490` (Meta+click で ARGB ⇄ HSB トグル) を新版に
// 移植する際の値域整合のためのヘルパ. 旧 Java 版は `Color.RGBtoHSB` の結果を
// `(int)(hsbValue[0] * 255)` で 0..255 に正規化していた (= alpha と同じ scale).
// 新版 ArgbSliderPanel も 4 本同一スケール (0..255) で内部統一するため, library の
// HsvaColor (h: 0..360, s/v: 0..100) からこの 0..255 表現への往復変換を提供する.
//
// 浮動小数誤差は ±1 LSB に収まる (= colorWheel.test.ts の round-trip と同水準).
//
// 設計判断 (consult 2026-05-07):
// - 表示用 `DisplayValues` (h: 0..360, s/v: 0..100) は ColorWheelPalette の数値タブで
//   使う高精度版. 一方こちらは ArgbSliderPanel の slider bar 専用 (= 0..255 一律).
// - 同じ HSV でも値域が違うため, 両者は別関数として分離する (= 取り違え事故の予防).

/** PenConfig.color (rgb24) を 0..255 正規化 HSV 各値に変換 (旧 Java `(hsb[*] * 255)` 踏襲). */
export const hsb255FromRgb24 = (rgb24: number): { h: number; s: number; v: number } => {
  // a は 1 固定 (HSV 計算で a は無視される).
  const hsva = rgbaToHsva({ r: r8(rgb24), g: g8(rgb24), b: b8(rgb24), a: 1 });
  return {
    h: clamp255(Math.round((hsva.h / 360) * 255)),
    s: clamp255(Math.round((hsva.s / 100) * 255)),
    v: clamp255(Math.round((hsva.v / 100) * 255)),
  };
};

/** 0..255 正規化 HSV → rgb24. ArgbSliderPanel HSB モードのスライダ操作経路. */
export const rgb24FromHsb255 = (h: number, s: number, v: number): number => {
  const hsva: HsvaColor = {
    h: clamp01(h / 255) * 360,
    s: clamp01(s / 255) * 100,
    v: clamp01(v / 255) * 100,
    a: 1,
  };
  return rgb24FromHsva(hsva);
};

/* ─── 表示用 (3 way: HSV / HSL / RGB) ────────────────────────────────────── */

/** RGBA 表記 (各 0..255 整数 + alpha 0..255 整数). */
export interface DisplayRgba {
  readonly mode: 'rgb';
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly a: number;
}
/** HSV 表記 (h: 0..360 整数 / s, v: 0..100 整数 / alpha 0..255 整数). */
export interface DisplayHsv {
  readonly mode: 'hsv';
  readonly h: number;
  readonly s: number;
  readonly v: number;
  readonly a: number;
}
/** HSL 表記 (h: 0..360 整数 / s, l: 0..100 整数 / alpha 0..255 整数). */
export interface DisplayHsl {
  readonly mode: 'hsl';
  readonly h: number;
  readonly s: number;
  readonly l: number;
  readonly a: number;
}

export type DisplayValues = DisplayRgba | DisplayHsv | DisplayHsl;

/**
 * HsvaColor を指定モード (HSV / HSL / RGB) の整数表記に変換.
 * 表示用なのですべて round で整数化する (旧版 `ColorMakeCanvas` の hex 表示も
 * 各 channel 整数値だった = 互換).
 */
export const toDisplayValues = (hsva: HsvaColor, mode: NumericMode): DisplayValues => {
  const a = clamp255(Math.round(hsva.a * 255));
  if (mode === 'rgb') {
    const rgba = hsvaToRgba(hsva);
    return {
      mode: 'rgb',
      r: clamp255(Math.round(rgba.r)),
      g: clamp255(Math.round(rgba.g)),
      b: clamp255(Math.round(rgba.b)),
      a,
    };
  }
  if (mode === 'hsv') {
    return {
      mode: 'hsv',
      h: Math.round(hsva.h) % 360,
      s: clampPct(Math.round(hsva.s)),
      v: clampPct(Math.round(hsva.v)),
      a,
    };
  }
  // hsl
  const hsla = hsvaToHsla(hsva);
  return {
    mode: 'hsl',
    h: Math.round(hsla.h) % 360,
    s: clampPct(Math.round(hsla.s)),
    l: clampPct(Math.round(hsla.l)),
    a,
  };
};

/**
 * 編集された数値表記 (RGB / HSV / HSL の **色成分のみ**) を HsvaColor に書き戻す.
 * 表記ごとの 1 成分編集 (Modern の自前 EditableInput 経路) で使う逆変換.
 *
 * **alpha は引数の `keepA` (0..1) を保持する** — 各表記の `a` フィールドは無視する
 * (= Modern では alpha をカラーパネルで操作しないため、表記側に A 欄を出さない / 仮に
 * 来ても採用しない、consult 2026-06-08)。値域は各成分を安全側に clamp する.
 */
export const hsvaFromDisplayValues = (values: DisplayValues, keepA: number): HsvaColor => {
  const a = clamp01(keepA);
  if (values.mode === 'rgb') {
    return rgbaToHsva({
      r: clamp255(Math.round(values.r)),
      g: clamp255(Math.round(values.g)),
      b: clamp255(Math.round(values.b)),
      a,
    });
  }
  if (values.mode === 'hsv') {
    return {
      h: ((Math.round(values.h) % 360) + 360) % 360,
      s: clampPct(Math.round(values.s)),
      v: clampPct(Math.round(values.v)),
      a,
    };
  }
  // hsl
  return hslaToHsva({
    h: ((Math.round(values.h) % 360) + 360) % 360,
    s: clampPct(Math.round(values.s)),
    l: clampPct(Math.round(values.l)),
    a,
  });
};

/* ─── HEX 入力 (双方向) ──────────────────────────────────────────────────── */

/**
 * HEX 文字列から HsvaColor を作る. invalid なら null. alpha は元値を保持したい
 * caller が居るので, 結果には a を含めず {h,s,v} だけ返す (= caller が a を保持).
 */
export const hsvFromHex = (hex: string): { h: number; s: number; v: number } | null => {
  if (!validHex(hex)) return null;
  const { h, s, v } = hexToHsva(hex);
  return { h, s, v };
};

/**
 * HsvaColor → 6 桁 HEX 文字列 (#RRGGBB). alpha は含めない (palette は alpha を
 * RGB24 として持っており, alpha は別 channel で扱う旧版踏襲).
 */
export const hexFromHsva = (hsva: HsvaColor): string => {
  // hsvaToHex は内部で hsvaToRgba → rgbToHex を呼ぶ (alpha 無視 = 6 桁 hex).
  return hsvaToHex(hsva).toLowerCase();
};

/* ─── 内部 clamp ヘルパ ──────────────────────────────────────────────────── */

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
const clamp255 = (v: number): number => Math.max(0, Math.min(255, v | 0));
const clampPct = (v: number): number => Math.max(0, Math.min(100, v | 0));

/* ─── controlled component sync (= 外部から描画色が変わったときの追従判定) ── */

/**
 * `ColorWheelPalette` を controlled component 化したときの「外部 value 更新を
 * 内部 HSVA に取り込むべきか」判定. caller (ClassicLayout) が **slot click 等で
 * 描画色を変えたとき** に panel 内 wheel/slider を追従させるが、**自分が発射した
 * onColorChange / onAlphaChange の echo** は無限ループになるので skip する.
 *
 * 戦略: panel が直前に発射した `{rgb24, alpha255}` を `lastEmitted` として保持,
 * 外部から流れてきた `external` がそれと一致したら「自分の echo」と判断して
 * 追従しない. 一致しなければ「外部由来の更新」として hsva を上書きする.
 *
 * @param external caller (= ClassicLayout) から `value` prop で流れてきた現在色
 * @param lastEmitted panel が直前に `onColorChange + onAlphaChange` で発射した値.
 *                    null は「まだ何も発射していない」or「直前の外部 update を
 *                    反映済 (= 次の自分発射に備えてクリア済)」を表す.
 * @returns true なら hsva を `external` で上書きすべき, false なら echo なので skip.
 */
export const shouldSyncFromExternalValue = (
  external: { readonly rgb24: number; readonly alpha255: number },
  lastEmitted: { readonly rgb24: number; readonly alpha255: number } | null,
): boolean => {
  if (lastEmitted === null) return true;
  return lastEmitted.rgb24 !== external.rgb24 || lastEmitted.alpha255 !== external.alpha255;
};

/* ─── round-trip 整合性 (テスト用) ───────────────────────────────────────── */

/**
 * 同じ色を 3 way で表記して再度 HsvaColor に戻したとき、rgb24 が一致するか.
 * テスト時に使う: rgb24 → hsva → hsl 表記 → hsva' → rgb24' で比較.
 *
 * `toRgba` / `toHsla` round-trip は浮動小数点誤差で 1〜2 微小ずれるが、255 整数化
 * 後はおおよそ一致する (= ±1 LSB 許容で OK の想定).
 */
export const rgb24RoundTrip = (rgb24: number, alpha255: number): number => {
  const hsva = hsvaFromRgb24Alpha255(rgb24, alpha255);
  return rgb24FromHsva(hsva);
};

/* ─── re-export for callers ──────────────────────────────────────────────── */

export type { HslaColor, HsvaColor, RgbaColor };
